import {readWireMinimumPrice} from "@/features/pricing";
import {Session} from "../../domain/session.entity";
import {
  SessionCurrency,
  BookingQuestion,
  SessionCoverImage,
} from "../../domain/session.types";
import {
  V2SessionResponse,
  V2SessionCurrency,
  V2SessionCustomerResponse,
  SessionCustomer,
  CreateSessionData,
  UpdateSessionData,
  V2CreateSessionPayload,
  V2UpdateSessionPayload,
} from "./session.mapper.types";

/**
 * Maps V2 API responses to Session domain entities.
 *
 * Key differences from V1:
 * - V2 API uses camelCase (no snake_case conversion needed)
 * - availabilitySchedule is a direct property (not "schedule")
 * - bookingQuestions is still a JSON string with answer_type snake_case inside
 *
 * Pure TypeScript - no React imports.
 */
export const SessionMapperV2 = {
  /**
   * Maps a single V2 API response to a Session entity.
   */
  toDomain(response: V2SessionResponse): Session {
    return Session.create({
      id: response.id,
      userId: response.userId,
      name: response.name,
      slug: response.slug,
      description: response.description,
      statusId: response.statusId,
      statusName: response.statusName,
      callDuration: response.callDuration,
      callPrice: response.callPrice,
      callPriceCurrency: response.callPriceCurrency,
      isPayWhatYouWant: response.isPayWhatYouWant ?? false,
      currencies: this.mapCurrencies(response.currencies),
      bookingQuestions: this.parseBookingQuestions(response.bookingQuestions),
      requestPhoneNumber: response.requestPhoneNumber,
      hideServiceFromMenu: response.hideServiceFromMenu,
      videoLocation: response.videoLocation,
      audioLocation: response.audioLocation,
      videoPlatform: response.videoPlatform ?? "",
      urlRedirect: response.urlRedirect,
      redirectType: response.redirectType,
      telegramGroupChatId: response.telegramGroupChatId,
      tinyUrl: response.tinyUrl,
      coverPicture: response.coverPicture,
      coverImages: (response.coverImages ?? []).map(
        (url, index): SessionCoverImage => ({id: `v2-cover-${index}`, url})
      ),
      enableCurrencyConversion: response.enableCurrencyConversion,
      availabilitySchedule: response.availabilitySchedule ?? null,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      isBundle: response.isBundle ?? false,
      numberOfSessions: response.noOfSessions ?? response.numberOfSessions ?? null,
      bookingInterval: response.bookingInterval ?? null,
      hasGoogleMeet:
        response.hasGoogleMeet === true || response.videoPlatform === "googleMeet",
    });
  },

  /**
   * Maps an array of V2 API responses to Session entities.
   */
  toDomainList(responses: V2SessionResponse[]): Session[] {
    return responses.map((response) => this.toDomain(response));
  },

  /**
   * Maps one row of a session's customers to a SessionCustomer.
   */
  toCustomer(row: V2SessionCustomerResponse): SessionCustomer {
    return {
      bookingReference: row.reference,
      name: [row.firstName, row.lastName].filter(Boolean).join(" "),
      email: row.email ?? "",
      lastBookingDate: row.bookingDate ?? "",
      lastBookingTime: row.bookingTime ?? "",
      currency: row.currency ?? "",
      amountPaid: row.price ?? 0,
    };
  },

  /**
   * Maps V2 currencies array (already camelCase).
   *
   * `minAmount` is the wire's spelling of the Minimum Price; `minCallPrice` is the
   * domain's. The rename stops here.
   */
  mapCurrencies(currencies?: V2SessionCurrency[]): SessionCurrency[] {
    if (!currencies) {
      return [];
    }

    return currencies.map((c) => ({
      currency: c.currency,
      amount: c.amount,
      minCallPrice: readWireMinimumPrice(c.minAmount),
      isAutoConverted: c.isAutoConverted,
    }));
  },

  /**
   * Parses booking questions JSON string to domain format.
   * Converts answer_type (snake_case in JSON) to answerType (camelCase).
   * Returns empty array on null, undefined, or parse failure (graceful degradation).
   */
  parseBookingQuestions(questionsJson?: string | null): BookingQuestion[] {
    if (!questionsJson) {
      return [];
    }

    try {
      const parsed = JSON.parse(questionsJson);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map(
        (q: {
          question: string;
          answer_type: string;
          required: boolean;
          options?: string[];
        }) => ({
          question: q.question,
          answerType: q.answer_type as BookingQuestion["answerType"],
          required: q.required,
          options: q.options,
        })
      );
    } catch {
      return [];
    }
  },

  /**
   * Converts domain CreateSessionData to V2 API payload.
   * V2 API uses camelCase but bookingQuestions still expects answer_type snake_case.
   */
  toCreatePayload(data: CreateSessionData): V2CreateSessionPayload {
    return {
      name: data.name,
      description: data.description,
      callDuration: data.callDuration,
      slug: data.slug,
      callPrice: data.callPrice,
      callPriceCurrency: data.callPriceCurrency,
      // Only Pay What You Want has a Minimum Price; the key is omitted rather than sent
      // as a zero the coach never typed.
      ...(data.minCallPrice === undefined ? {} : {minCallPrice: data.minCallPrice}),
      ...(data.isPayWhatYouWant === undefined
        ? {}
        : {isPayWhatYouWant: data.isPayWhatYouWant}),
      // The base currency stays in the scalars above and must not be repeated here.
      currencies: data.currencies,
      ...(data.redirectType && data.redirectType !== "none"
        ? {
            urlRedirect: data.urlRedirect,
            redirectType: data.redirectType,
            telegramGroupChatId: data.telegramGroupChatId,
          }
        : {}),
      audioLocation: data.audioLocation,
      videoLocation: data.videoLocation,
      ...(data.videoPlatform ? {videoPlatform: data.videoPlatform} : {}),
      hideServiceFromMenu: data.hideServiceFromMenu,
      requestPhoneNumber: data.requestPhoneNumber,
      coverImage: data.coverImage,
      bookingQuestions: data.bookingQuestions.map((q) => ({
        question: q.question,
        answer_type: q.answerType,
        required: q.required,
        options: q.options,
      })),
      availabilityScheduleId: data.availabilityScheduleId,
      enableCurrencyConversion: data.enableCurrencyConversion,
      selectedCurrencies: data.selectedCurrencies,
      ...(data.hasGoogleMeet !== undefined && {hasGoogleMeet: data.hasGoogleMeet}),
      ...(data.noOfSessions !== undefined && {noOfSessions: data.noOfSessions}),
      ...(data.bookingInterval !== undefined && {bookingInterval: data.bookingInterval}),
    };
  },

  /**
   * Converts domain UpdateSessionData to V2 API payload.
   * Reuses toCreatePayload and adds the id field.
   */
  toUpdatePayload(data: UpdateSessionData): V2UpdateSessionPayload {
    return {
      ...this.toCreatePayload(data),
      id: data.id,
      ...(data.isPaused !== undefined && {isPaused: data.isPaused}),
    };
  },
};
